/**
 * Procedência da oferta — de qual busca este preço veio.
 *
 * Lógica pura. Sem React, sem fetch, sem estado.
 *
 * O PROBLEMA QUE ESTE MÓDULO RESOLVE
 * ──────────────────────────────────
 * A Moblix devolve `price.total` como TOTAL DO GRUPO, já calculado para a
 * quantidade de passageiros enviada na busca. Medido em 39 ofertas reais
 * (VDC→GRU, 02/10→09/10): 2 ADT = exatamente 2× o total de 1 ADT, 3 ADT =
 * exatamente 3×. O preço é correto na origem e chega correto na tela — o
 * caminho inteiro é passagem direta, sem transformação.
 *
 * O que estava errado era a PROCEDÊNCIA. `offers` e `adults` viviam como dois
 * estados independentes em `AereoPage`. O stepper de passageiros mudava
 * `adults` e não tocava em `offers`; `setOffers([])` só acontecia em
 * `handleSearch` e `handleSwap`. Então:
 *
 *     busca 1 ADT       → oferta = R$ 1.659,50
 *     vendedor põe 3 ADT → adults = 3, oferta INTACTA
 *     Montar Cotação     → cotação de 3 pessoas com preço de 1
 *
 * A Rextur cobraria R$ 4.978,50. A cotação saía com R$ 1.659,50 — 66,7% a
 * menos — e a tela ainda escrevia "base · 3 pax" ao lado do número, afirmando
 * a coerência que não existia.
 *
 * POR QUE NÃO É UM useEffect QUE LIMPA `offers`
 * ─────────────────────────────────────────────
 * Limpar ofertas quando `adults` muda resolve ESTE caminho e deixa a doença: a
 * oferta continua sendo um objeto que não sabe de quantas pessoas ele fala.
 * Qualquer caminho novo — navegação de volta, cache, o próximo refactor de UI —
 * recria o mesmo bug, porque nada no dado impede a incoerência.
 *
 * Aqui a procedência viaja COM a oferta. Uma oferta cuja procedência não bate
 * com a busca atual não é "uma oferta com preço velho": é uma oferta que não
 * responde à pergunta que está sendo feita. Não dá para usar, e não dá para
 * consertar com aritmética — só uma busca nova produz o preço daquele grupo.
 *
 * O QUE NÃO SE FAZ AQUI
 * ─────────────────────
 * Este módulo não toca em preço. Não multiplica por passageiros, não mexe em
 * `commission`, `taxes` ou RAV, não reconstrói total. A investigação provou
 * que a aritmética está certa; o defeito é de origem do dado.
 */

/** Campos que definem a identidade de uma busca. Ordem = ordem de leitura. */
const CAMPOS = [
  'origin', 'destination', 'departureDate', 'returnDate',
  'adults', 'children', 'infants',
];

const ROTULOS = {
  origin: 'origem',
  destination: 'destino',
  departureDate: 'data de ida',
  returnDate: 'data de volta',
  adults: 'adultos',
  children: 'crianças',
  infants: 'bebês',
};

const texto = (v) => (v === null || v === undefined || v === '' ? null : String(v).trim().toUpperCase());
const data = (v) => (v === null || v === undefined || v === '' ? null : String(v).trim());

function inteiro(v, minimo) {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n >= minimo ? n : minimo;
}

/**
 * Congela os parâmetros de uma busca num objeto comparável.
 *
 * `roundTrip` não entra na procedência como campo próprio: ele já está
 * expresso em `returnDate`. Com o checkbox desmarcado a volta é `null`, mesmo
 * que o campo de data ainda tenha texto — senão uma data órfã no input faria
 * duas buscas idênticas parecerem diferentes.
 *
 * NOTA sobre `children`/`infants` (P3, fora deste escopo): o backend hoje
 * descarta esses campos em `_validate_payload`, então a busca que chega à
 * Moblix é só de adultos. A procedência registra o que o VENDEDOR selecionou,
 * que é o correto para invalidar: se ele mexe em crianças, a oferta anterior
 * deixa de responder à pergunta atual. Quando o P3 for corrigido, nada aqui
 * muda.
 */
export function criarProcedencia(busca) {
  const b = busca || {};
  const temVolta = b.roundTrip === undefined ? Boolean(b.returnDate) : Boolean(b.roundTrip);
  return {
    origin: texto(b.origin),
    destination: texto(b.destination),
    departureDate: data(b.departureDate),
    returnDate: temVolta ? data(b.returnDate) : null,
    adults: inteiro(b.adults, 1),
    children: inteiro(b.children, 0),
    infants: inteiro(b.infants, 0),
  };
}

/** Duas procedências descrevem a mesma busca? */
export function mesmaProcedencia(a, b) {
  if (!a || !b) return false;
  return CAMPOS.every((c) => a[c] === b[c]);
}

/**
 * O que mudou entre a busca que gerou as ofertas e a busca atual.
 * Devolve [] quando são compatíveis. Serve para a mensagem na tela dizer ao
 * vendedor exatamente o que ele mexeu.
 */
export function diferencas(procedenciaDaBusca, procedenciaAtual) {
  if (!procedenciaDaBusca || !procedenciaAtual) return [];
  return CAMPOS
    .filter((c) => procedenciaDaBusca[c] !== procedenciaAtual[c])
    .map((c) => ({
      campo: c,
      rotulo: ROTULOS[c],
      de: procedenciaDaBusca[c],
      para: procedenciaAtual[c],
    }));
}

/** Frase curta para o aviso na tela. */
export function motivoDaInvalidez(procedenciaDaBusca, procedenciaAtual) {
  const d = diferencas(procedenciaDaBusca, procedenciaAtual);
  if (d.length === 0) return null;
  const partes = d.map(({ rotulo, de, para }) => `${rotulo}: ${de ?? '—'} → ${para ?? '—'}`);
  return partes.join(' · ');
}

/**
 * Esta oferta pode ser usada para a busca atual?
 *
 * Uma oferta SEM procedência é recusada. Oferta antiga, de sessão anterior ou
 * de código que ainda não carimba, não pode passar por omissão — silêncio não
 * é permissão quando o que está em jogo é preço mostrado ao cliente.
 */
export function ofertaUtilizavel(oferta, procedenciaAtual) {
  if (!oferta || !procedenciaAtual) return false;
  return mesmaProcedencia(oferta._procedencia, procedenciaAtual);
}

/** Carimba um lote de ofertas recém-chegadas com a procedência da busca. */
export function carimbar(ofertas, procedencia) {
  if (!Array.isArray(ofertas)) return [];
  return ofertas.map((o) => ({ ...o, _procedencia: procedencia }));
}

/** Todas as ofertas do carrinho servem para a busca atual? */
export function carrinhoUtilizavel(carrinho, procedenciaAtual) {
  const c = carrinho || {};
  const todas = [...(c.outbound || []), ...(c.return || [])];
  if (todas.length === 0) return true;
  return todas.every((o) => ofertaUtilizavel(o, procedenciaAtual));
}
